import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import ContactForm from './components/ContactForm';
import LocationMap from './components/LocationMap';
import StaffIntroduction from './components/StaffIntroduction';
import FAQSection from './components/FAQSection';
import SocialFeed from './components/SocialFeed';

const ContactLocation = () => {
  const [activeTab, setActiveTab] = useState('contact');

  const contactInfo = {
    phone: "+1 (555) 123-KEBAB",
    email: "hello@kebabpalace.com",
    address: "1234 Mediterranean Avenue, Culinary District, CA 90210",
    hours: {
      weekdays: "11:00 AM - 10:00 PM",
      weekends: "10:00 AM - 11:00 PM",
      holidays: "12:00 PM - 9:00 PM"
    }
  };

  const quickActions = [
    {
      icon: "Phone",
      title: "Call Now",
      subtitle: "Speak with our team",
      action: `tel:${contactInfo.phone}`,
      color: "bg-conversion text-white"
    },
    {
      icon: "MapPin",
      title: "Get Directions",
      subtitle: "Navigate to our location",
      action: "https://maps.google.com/?q=34.0522,-118.2437",
      color: "bg-primary text-white"
    },
    {
      icon: "Calendar",
      title: "Make Reservation",
      subtitle: "Book your table",
      action: "/reservation",
      color: "bg-accent text-white"
    },
    {
      icon: "ShoppingBag",
      title: "Order Online",
      subtitle: "Fresh food delivered",
      action: "/order-online-streamlined-experience",
      color: "bg-trust text-white"
    }
  ];

  const tabs = [
    { id: 'contact', label: 'Contact Info', icon: 'MessageCircle' },
    { id: 'location', label: 'Location & Map', icon: 'MapPin' },
    { id: 'team', label: 'Meet Our Team', icon: 'Users' },
    { id: 'faq', label: 'FAQ', icon: 'HelpCircle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-500 to-secondary-600 text-white pt-24 pb-16">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-headline text-4xl md:text-6xl font-bold mb-6">
              Visit Kebab Palace
            </h1>
            <p className="font-body text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
              Where Mediterranean warmth meets modern hospitality. We're here to welcome you home.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Icon name="Star" size={20} className="text-accent fill-current" />
                <span className="font-semibold">4.8/5 Rating</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Icon name="Clock" size={20} className="text-accent" />
                <span className="font-medium">Open Daily</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Icon name="MapPin" size={20} className="text-accent" />
                <span className="font-medium">Culinary District</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {quickActions.map((action, index) => (
              <a
                key={index}
                href={action.action.startsWith('/') ? undefined : action.action}
                onClick={action.action.startsWith('/') ? (e) => {
                  e.preventDefault();
                  window.location.href = action.action;
                } : undefined}
                className={`${action.color} p-6 rounded-xl shadow-warm hover:shadow-warm-lg transition-organic group text-center`}
              >
                <Icon name={action.icon} size={32} className="mx-auto mb-3 group-hover:scale-110 transition-organic" />
                <h3 className="font-cta font-semibold text-lg mb-1">{action.title}</h3>
                <p className="text-sm opacity-90">{action.subtitle}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-surface border-b border-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 py-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-medium transition-organic ${
                  activeTab === tab.id
                    ? 'bg-primary text-white shadow-warm'
                    : 'text-text-primary hover:text-primary hover:bg-primary-50'
                }`}
              >
                <Icon name={tab.icon} size={20} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information Tab */}
      {activeTab === 'contact' && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Details */}
              <div className="space-y-8">
                <div>
                  <h2 className="font-headline text-3xl font-bold text-text-primary mb-6">
                    Get in Touch
                  </h2>
                  <p className="font-body text-lg text-text-secondary mb-8">
                    We're here to make your Mediterranean dining experience exceptional. Reach out for reservations, catering, or just to say hello!
                  </p>
                </div>

                {/* Contact Methods */}
                <div className="space-y-6">
                  <div className="flex items-start space-x-4 p-6 bg-surface rounded-xl shadow-warm">
                    <div className="bg-primary-100 p-3 rounded-lg">
                      <Icon name="Phone" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-cta font-semibold text-lg text-text-primary mb-1">Phone</h3>
                      <a href={`tel:${contactInfo.phone}`} className="text-primary hover:text-primary-600 font-medium">
                        {contactInfo.phone}
                      </a>
                      <p className="text-sm text-text-secondary mt-1">Click to call on mobile</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-6 bg-surface rounded-xl shadow-warm">
                    <div className="bg-accent-100 p-3 rounded-lg">
                      <Icon name="Mail" size={24} className="text-accent-600" />
                    </div>
                    <div>
                      <h3 className="font-cta font-semibold text-lg text-text-primary mb-1">Email</h3>
                      <a href={`mailto:${contactInfo.email}`} className="text-accent-600 hover:text-accent-700 font-medium">
                        {contactInfo.email}
                      </a>
                      <p className="text-sm text-text-secondary mt-1">We respond within 2 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-6 bg-surface rounded-xl shadow-warm">
                    <div className="bg-trust-100 p-3 rounded-lg">
                      <Icon name="MapPin" size={24} className="text-trust-600" />
                    </div>
                    <div>
                      <h3 className="font-cta font-semibold text-lg text-text-primary mb-1">Address</h3>
                      <p className="text-text-primary font-medium">{contactInfo.address}</p>
                      <p className="text-sm text-text-secondary mt-1">Free parking available</p>
                    </div>
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-gradient-to-r from-primary-50 to-accent-50 p-6 rounded-xl">
                  <h3 className="font-headline text-xl font-bold text-text-primary mb-4 flex items-center">
                    <Icon name="Clock" size={24} className="text-primary mr-3" />
                    Hours of Operation
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium text-text-primary">Monday - Friday</span>
                      <span className="text-text-secondary">{contactInfo.hours.weekdays}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-text-primary">Saturday - Sunday</span>
                      <span className="text-text-secondary">{contactInfo.hours.weekends}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-text-primary">Holidays</span>
                      <span className="text-text-secondary">{contactInfo.hours.holidays}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Location & Map Tab */}
      {activeTab === 'location' && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LocationMap />
          </div>
        </section>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <StaffIntroduction />
          </div>
        </section>
      )}

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FAQSection />
          </div>
        </section>
      )}

      {/* Social Media Feed */}
      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SocialFeed />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-headline text-3xl md:text-4xl font-bold mb-6">
            Ready to Experience Authentic Mediterranean?
          </h2>
          <p className="font-body text-xl text-white/90 mb-8">
            Join our family for an unforgettable culinary journey that celebrates tradition and flavor.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/order-online-streamlined-experience"
              className="btn-conversion text-lg px-8 py-4"
            >
              Order Fresh Now
            </Link>
            <a
              href={`tel:${contactInfo.phone}`}
              className="bg-white text-primary hover:bg-primary-50 font-cta font-semibold px-8 py-4 rounded-lg shadow-warm transition-organic text-lg"
            >
              Call for Reservations
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactLocation;